import { useState } from 'react';

import { CustomerGroupSettingsMessage } from '../../../../../../types/CustomerGroupSettingsMessage';
import { SamlGroupMapping } from '../../../../../../types/SsoConfiguration';
import { IconButton } from '../../../../../cdl/IconButton/IconButton';
import { TableBuilder } from '../../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../../cdl/TableBuilder/TableBuilderColumn';
import { Tag } from '../../../../../cdl/Tag/Tag';
import { useToasts } from '../../../../../cdl/Toast/useToasts';
import { CompanyListing } from '../../../../../common/CompanyListing/CompanyListing';
import { DeleteConfirmPopup } from '../../../../../common/DeleteConfirmPopup/DeleteConfirmPopup';
import { identifyUserRole } from '../../../../../common/helpers/identifyUserRole.helper';
import { translate } from '../../../../../common/helpers/translate.helper';
import { useEnums } from '../../../../../common/hooks/useEnums';
import { IconPencil } from '../../../../../common/icons/cdl/Pencil';
import { IconTrash } from '../../../../../common/icons/cdl/Trash';
import { Box } from '../../../../../common/ui/Box';
import { SsoGroup, transformGroupMappingToArray } from '../../../../utils/transformGroupMapping';
import { useCustomerGroupSettings } from '../../hooks/useCustomerGroupSettings';
import { useCustomerGroupSettingsUpdate } from '../../hooks/useCustomerGroupSettingsUpdate';
import { useCustomerList } from '../../hooks/useCustomerList';

import { CustomerEditSsoGroupModal } from './CustomerEditSsoGroupModal';

interface CustomerSsoGroupsTableProps {
    samlGroupMapping: SamlGroupMapping;
    groupId: string;
    readOnly?: boolean;
}

export const CustomerSsoGroupsTable = ({
    samlGroupMapping,
    groupId,
    readOnly = false,
}: CustomerSsoGroupsTableProps) => {
    const settingsQuery = useCustomerGroupSettings(groupId);
    const updateMutation = useCustomerGroupSettingsUpdate(groupId);
    const companiesQuery = useCustomerList({ groupId });
    const ssoGroups = transformGroupMappingToArray(samlGroupMapping);
    const { getHumanReadableValue } = useEnums();
    const { addToast, addErrorToast } = useToasts();

    const [editingGroup, setEditingGroup] = useState<SsoGroup | null>(null);
    const [deletingGroup, setDeletingGroup] = useState<SsoGroup | null>(null);

    const handleDeleteGroup = async () => {
        if (!deletingGroup || !settingsQuery.data) return;

        const newSamlGroupMapping = { ...settingsQuery.data.ssoConfiguration!.samlGroupMapping };
        delete newSamlGroupMapping[deletingGroup.groupName];

        const updatedSettings: CustomerGroupSettingsMessage = {
            ...settingsQuery.data,
            ssoConfiguration: {
                ...settingsQuery.data.ssoConfiguration!,
                samlGroupMapping: newSamlGroupMapping,
            },
        };

        try {
            await updateMutation.mutateAsync({
                groupId,
                settings: updatedSettings,
            });
            addToast(translate('sso.deleteGroupSuccess', { groupName: deletingGroup.groupName }));
            setDeletingGroup(null);
        } catch {
            addErrorToast(translate('sso.deleteGroupError'));
        }
    };

    return (
        <>
            <TableBuilder data={ssoGroups}>
                <TableBuilderColumn<SsoGroup> header={translate('sso.table.groupName')} width="40%">
                    {(group) => group.groupName}
                </TableBuilderColumn>

                <TableBuilderColumn<SsoGroup> header={translate('sso.table.role')} width="20%">
                    {(group) => {
                        const role = identifyUserRole(group.accessLevel, group.companyAccesses);
                        return <Tag variant="default">{getHumanReadableValue('role', role)}</Tag>;
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<SsoGroup>
                    header={translate('sso.table.companies')}
                    width={readOnly ? '40%' : '30%'}
                >
                    {(group) => {
                        const companyIds = group.companyAccesses?.map((ca) => ca.companyId) || [];
                        const groupCompanies = companiesQuery.data?.filter((company) =>
                            companyIds.includes(company.id)
                        );

                        if (!groupCompanies) {
                            return null;
                        }

                        return <CompanyListing companies={groupCompanies} />;
                    }}
                </TableBuilderColumn>

                {!readOnly && (
                    <TableBuilderColumn<SsoGroup>
                        header={translate('sso.table.actions')}
                        width="10%"
                        interactive
                        numeric
                    >
                        {(group) => (
                            <Box display="flex" gap={4} justifyContent="flex-end">
                                <IconButton Icon={IconPencil} onClick={() => setEditingGroup(group)} />
                                <IconButton Icon={IconTrash} onClick={() => setDeletingGroup(group)} />
                            </Box>
                        )}
                    </TableBuilderColumn>
                )}
            </TableBuilder>

            {!readOnly && (
                <>
                    {editingGroup && (
                        <CustomerEditSsoGroupModal
                            isOpen={!!editingGroup}
                            onDismiss={() => setEditingGroup(null)}
                            groupId={groupId}
                            group={editingGroup}
                        />
                    )}

                    <DeleteConfirmPopup
                        isOpen={!!deletingGroup}
                        onDismiss={() => setDeletingGroup(null)}
                        onSubmit={handleDeleteGroup}
                        headline={translate('sso.deleteGroupModal.title')}
                        text={translate('sso.deleteGroupModal.description')}
                        itemToDelete={deletingGroup?.groupName}
                        deleteButtonText={translate('sso.deleteGroupButton')}
                        isSubmitting={updateMutation.isPending}
                    />
                </>
            )}
        </>
    );
};
